import { useLazyQuery } from '@apollo/client';
import { SettingsGroup, SettingsItemAutocomplete } from '@elipssolution/harfang';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ConnectorFieldsForm from './ConnectorFieldsForm';
import { FETCH_CONNECTORS, FetchConnectorsType } from '../api/connector';
import useDocumentConnector from '../hooks/useDocumentConnector';
import { ConnectorType } from '../types/connector';
import { DocumentConnectorCodeEnum } from '../types/documentConnectorCode';

export type ConnectorFormType = {
	connector?: ConnectorType['code'];
	configuration?: Record<string, string | number | undefined> | null;
};

type ConnectorFormProps = {
	initialValues?: ConnectorFormType;
	type: string;
	onValuesChange: (value?: ConnectorFormType) => void;
	onConfigurationValidityChange: (value: boolean) => void;
};

const ConnectorForm = ({ type, initialValues, onValuesChange, onConfigurationValidityChange }: ConnectorFormProps) => {
	const {
		control,
		formState: { isDirty },
		reset,
		setValue,
		watch,
	} = useForm<ConnectorFormType>();

	const configurationForm = watch('configuration');
	const connectorForm = watch('connector');

	const [fetchConnectors, { data: connectorsData }] = useLazyQuery<FetchConnectorsType>(FETCH_CONNECTORS, {
		onCompleted: ({ document_connectors }) => {
			const { items } = document_connectors;
			if (!connectorForm) {
				const defaultConnector = items.find(({ code }) => code === 'agiris-fidorg') ?? items[0];

				setValue('connector', defaultConnector.code);
			}
		},
	});
	const connectors = useMemo(() => connectorsData?.document_connectors.items ?? [], [connectorsData]);

	const connector = useMemo(() => {
		if (connectorForm) return connectors.find(({ code }) => code === connectorForm);

		return connectors?.find(({ code }) => code === 'agiris-fidorg') ?? connectors?.[0];
	}, [connectorForm, connectors]);

	const { connector: documentConnector } =
		useDocumentConnector({ variables: { code: connector?.code || DocumentConnectorCodeEnum.SFTP } }) || {};

	const connectorCodesDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: string[];
		}> => {
			const { data, error: queryError } = await fetchConnectors({
				variables: {
					...(type === 'upload' ? { hasWrite: true } : { hasRead: true }),
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (queryError) {
				throw queryError;
			}

			const {
				document_connectors: { count = 0, items: fetchedConnectors = [] },
			} = data ?? { document_connectors: {} };

			return {
				items: fetchedConnectors.map(({ code }) => code),
				count,
			};
		},
		[fetchConnectors, type],
	);

	useEffect(() => {
		if (initialValues) reset(initialValues);
	}, [initialValues, reset]);

	useEffect(() => {
		if (!connector) return;

		const { parameters } = connector;

		const requiredParameterCodes: string[] =
			parameters?.flatMap(({ code, isRequired }) => (isRequired ? [code] : [])) ?? [];

		const isConfigurationValid = requiredParameterCodes.every((code) => !!configurationForm?.[code]);

		onConfigurationValidityChange(isConfigurationValid && isDirty);
	}, [configurationForm, connector, isDirty, onConfigurationValidityChange]);

	return (
		<SettingsGroup label="Choix du connecteur">
			<Controller
				control={control}
				render={({ field: { onChange, ...field } }) => {
					const handleChange = (value?: string) => {
						onChange(value);
						setValue('configuration', null);
						onValuesChange({ connector: connectors.find(({ code }) => code === value)?.code });
					};

					return (
						<SettingsItemAutocomplete<string>
							{...field}
							dataSource={connectorCodesDataSource}
							description="Connecteur de l'emplacement de dépôt."
							onChange={handleChange}
							getOptionLabel={(option) => option ?? ''}
							label="Connecteur"
							disableClearable
							required
						/>
					);
				}}
				rules={{ required: true }}
				name="connector"
			/>

			<Controller
				control={control}
				render={({ field: { onChange, ...fields } }) => (
					<ConnectorFieldsForm
						{...fields}
						fields={documentConnector?.parameters}
						onChange={(value) => {
							onChange(value);
							onValuesChange({ connector: connector?.code, configuration: value });
						}}
					/>
				)}
				name="configuration"
			/>
		</SettingsGroup>
	);
};

export default ConnectorForm;
